import React, { useCallback, useMemo } from "react";
import { Form } from "react-bootstrap";
import Checklist from "../Checklist";

const JobInformationSection = (props) => {
  const { bundlesList, user, setUser, updateLocationsSupporting, mode, typeOfModal } = props;
  const defaultOptions = useMemo(
    () => ({
      significantDigits: 2,
      thousandsSeparator: ",",
      decimalSeparator: ".",
      symbol: "$",
    }),
    [],
  );

  const currencyFormatter = useCallback(
    (value, options) => {
      if (typeof value !== "number") {
        value = 0.0;
      }
      options = { ...defaultOptions, ...options };
      value = value.toFixed(options.significantDigits);

      const [currency, decimal] = value.split(".");
      return `${options.symbol}${currency.replace(/\B(?=(\d{3})+(?!\d))/g, options.thousandsSeparator)}${
        options.decimalSeparator
      }${decimal}`;
    },
    [defaultOptions],
  );

  const licenseBundleName = useCallback(
    (licenseBundle, licenseBundleName) => {
      return (
        <span>
          <i>
            <u>
              License Bundle: {licenseBundle} (
              {currencyFormatter(
                bundlesList.filter((bundle) => bundle.bundleName === licenseBundleName)[0].bundlePrice,
              )}
              )
            </u>
          </i>
        </span>
      );
    },
    [bundlesList, currencyFormatter],
  );

  const renderAccessDetails = (accessDetails) => {
    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        {accessDetails.map((info, index) => {
          return (
            <div
              style={
                index !== accessDetails.length - 1
                  ? {
                      display: "flex",
                      flexDirection: "column",
                      gap: "5px",
                      padding: "10px 0px",
                      borderBottom: "1px solid #CED4DA",
                    }
                  : { display: "flex", flexDirection: "column", gap: "5px", paddingTop: "10px" }
              }
            >
              <span>
                <u>{info.header}</u>
              </span>
              {info.lines.map((line) => {
                if (line.type === "text") {
                  return (
                    <div>
                      <span> - {line.label}: </span> {line.value}
                    </div>
                  );
                } else if (line.type === "dropdown") {
                  return (
                    <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                      <span> - {line.label}: </span>
                      <Form.Select style={{ width: "220px" }} onChange={(e) => line.onChange(e.target.value)}>
                        {line.options.map((option) => (
                          <option>{option}</option>
                        ))}
                      </Form.Select>
                    </div>
                  );
                } else if (line.type === "checkbox") {
                  return (
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        alignItems: "center",
                      }}
                    >
                      <span> - {line.label}</span>
                      {line.value}
                      <Form.Check onClick={line.onClick} checked={line.checked} />
                    </div>
                  );
                } else if (line.type === "checklist") {
                  return <Checklist checkboxes={line.checkboxes} maxHeight="150px" />;
                } else {
                  return <></>;
                }
              })}
            </div>
          );
        })}
      </div>
    );
  };

  const jobs = useMemo(
    () => [
      {
        jobTitle: "Accountant",
        header: licenseBundleName("Accountant", "Accounting"),
        systemsIncluded: "CRM, Email, Power BI, Solver, BC, Avalara",
        accessDetails: [
          {
            header: "Business Central",
            lines: [
              {
                type: "dropdown",
                label: "Security Role",
                options: ["Accounting Clerk", "Collections", "Controller", "Solver"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    businessCentralSecurityRole: role,
                  })),
              },
            ],
          },
          {
            header: "CRM",
            lines: [
              {
                type: "text",
                label: "Contract & Financial Details",
                value: "Full Edit Access",
              },
              {
                type: "text",
                label: "Ability to Approve Clients and Contracts",
                value: "Yes",
              },
            ],
          },
          {
            header: "Field Sales",
            lines: [
              {
                type: "text",
                label: "Access",
                value: "Power User",
              },
            ],
          },
          {
            header: "Supporting Locations",
            lines: [
              {
                type: "checklist",
                checkboxes: [
                  {
                    label: "KC",
                    onClick: () => updateLocationsSupporting("KC"),
                    checked: user.locationsSupporting.includes("KC"),
                  },
                  {
                    label: "Window Washing",
                    onClick: () => updateLocationsSupporting("Window Washing"),
                    checked: user.locationsSupporting.includes("Window Washing"),
                  },
                  {
                    label: "Silicon Valley",
                    onClick: () => updateLocationsSupporting("Silicon Valley"),
                    checked: user.locationsSupporting.includes("Silicon Valley"),
                  },
                  {
                    label: "N. VA",
                    onClick: () => updateLocationsSupporting("N. VA"),
                    checked: user.locationsSupporting.includes("N. VA"),
                  },
                  {
                    label: "Minnesota",
                    onClick: () => updateLocationsSupporting("Minnesota"),
                    checked: user.locationsSupporting.includes("Minnesota"),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        jobTitle: "Administrative Assistant",
        header: !(mode === "update" && typeOfModal === "Franchise") ? (
          licenseBundleName("Admin", "Administrative User")
        ) : (
          <p>
            <strong>
              <i>
                <u>License Bundle: Admin ($90.00)</u>
              </i>
            </strong>
          </p>
        ),
        systemsIncluded: "CRM, Email, Power BI",
        accessDetails: [
          {
            header: "CRM",
            lines: [
              {
                type: "dropdown",
                label: "Contract & Financial Details",
                options: ["Full Edit Access", "Read Only"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    crmContractAndFinancialDetailsAccess: role,
                  })),
              },
              {
                type: "checkbox",
                label: "Ability to Approve Clients and Contracts",
                onClick: () =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    crmClientsAndContracts: !user.crmClientsAndContracts,
                  })),
                checked: user.crmClientsAndContracts,
              },
            ],
          },
          {
            header: "Field Sales",
            lines: [{ type: "text", label: "Access", value: "Power User" }],
          },
        ],
      },
      {
        jobTitle: "Franchisee",
        header: licenseBundleName("Executive", "Executive"),
        systemsIncluded: "CRM, Email, Power BI, Solver",
        accessDetails: [
          {
            header: "CRM",
            lines: [
              { type: "text", label: "Contract & Financial Details", value: "Full Edit Access" },
              { type: "text", label: "Has Ability to Approve Clients and Contracts", value: "Yes" },
            ],
          },
          {
            header: "Field Sales",
            lines: [{ type: "text", label: "Access", value: "Power User" }],
          },
        ],
      },
      {
        jobTitle: "Business Development Specialist",
        header: licenseBundleName("Sales", "Sales"),
        systemsIncluded: "CRM, Email",

        accessDetails: [
          {
            header: "CRM",
            lines: [{ type: "text", label: "Contract & Financial Details", value: "Full Edit Access" }],
          },
        ],
      },
      {
        jobTitle: "Director of Sales",
        header: licenseBundleName("Sales", "Sales"),
        systemsIncluded: "CRM, Email, Power BI",
        accessDetails: [
          {
            header: "CRM",
            lines: [{ type: "text", label: "Contract & Financial Details", value: "Full Edit Access" }],
          },
        ],
      },
      {
        jobTitle: "Sales Manager",
        header: licenseBundleName("Sales", "Sales"),
        systemsIncluded: "CRM, Email, Power BI",
        accessDetails: [
          {
            header: "CRM",
            lines: [{ type: "text", label: "Contract & Financial Details", value: "Full Edit Access" }],
          },
        ],
      },
      {
        jobTitle: "Sr. Sales Executive",
        header: licenseBundleName("Sales", "Sales"),
        systemsIncluded: "CRM, Email, Power BI",
        accessDetails: [
          {
            header: "CRM",
            lines: [{ type: "text", label: "Contract & Financial Details", value: "Full Edit Access" }],
          },
        ],
      },
      {
        jobTitle: "Sales Executive",
        header: licenseBundleName("Sales", "Sales"),
        systemsIncluded: "CRM, Email, Power BI",
        accessDetails: [
          {
            header: "CRM",
            lines: [{ type: "text", label: "Contract & Financial Details", value: "Full Edit Access" }],
          },
        ],
      },
      {
        jobTitle: "Jr. Sales Executive",
        header: licenseBundleName("Sales", "Sales"),
        systemsIncluded: "CRM, Email, Power BI",
        accessDetails: [
          {
            header: "CRM",
            lines: [{ type: "text", label: "Contract & Financial Details", value: "Full Edit Access" }],
          },
        ],
      },
      {
        jobTitle: "Sales Associate",
        header: licenseBundleName("Sales", "Sales"),
        systemsIncluded: "CRM, Email, Power BI",
        accessDetails: [
          {
            header: "CRM",
            lines: [
              {
                type: "dropdown",
                label: "Contract & Financial Details",
                options: ["Full Edit Access", "Read Only"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    crmContractAndFinancialDetailsAccess: role,
                  })),
              },
            ],
          },
        ],
      },
      {
        jobTitle: "Operations Manager",
        header: licenseBundleName("OPS", "Operations"),
        systemsIncluded: "CRM, Email, Power BI",
        accessDetails: [
          {
            header: "CRM",
            lines: [
              {
                type: "dropdown",
                label: "Contract & Financial Details",
                options: ["Full Edit Access", "Read Only"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    crmContractAndFinancialDetailsAccess: role,
                  })),
              },
            ],
          },
          {
            header: "Field Sales",
            lines: [
              {
                type: "dropdown",
                label: "Access",
                options: ["Power User", "DOO"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    fieldSalesAccess: role,
                  })),
              },
            ],
          },
        ],
      },
      {
        jobTitle: "Director of Operations",
        header: licenseBundleName("Executive", "Executive"),
        systemsIncluded: "CRM, Email, Power BI, Solver",
        accessDetails: [
          {
            header: "CRM",
            lines: [
              {
                type: "dropdown",
                label: "Contract & Financial Details",
                options: ["Full Edit Access", "Read Only"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    crmContractAndFinancialDetailsAccess: role,
                  })),
              },
            ],
          },
          {
            header: "Field Sales",
            lines: [
              {
                type: "dropdown",
                label: "Access",
                options: ["Power User", "DOO"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    fieldSalesAccess: role,
                  })),
              },
            ],
          },
        ],
      },
      {
        jobTitle: "Facility Solutions Manager",
        header: licenseBundleName("Operations", "Operations"),
        systemsIncluded: "CRM, Email, Power BI",
        accessDetails: [
          {
            header: "CRM",
            lines: [
              {
                type: "dropdown",
                label: "Contract & Financial Details",
                options: ["Full Edit Access", "Read Only"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    crmContractAndFinancialDetailsAccess: role,
                  })),
              },
            ],
          },
          {
            header: "Field Sales",
            lines: [
              {
                type: "text",
                label: "Access",
                value: "FSM",
              },
            ],
          },
        ],
      },
      {
        jobTitle: "Night Manager",
        header: licenseBundleName("Night Manager", "Night Manager"),
        systemsIncluded: "CRM, Email",
      },
      {
        jobTitle: "CBS Sales Executive",
        header: licenseBundleName("OPS", "Operations"),
        systemsIncluded: "CRM, Email, Power BI",
        accessDetails: [
          {
            header: "CRM",
            lines: [
              {
                type: "dropdown",
                label: "Contract & Financial Details",
                options: ["Full Edit Access", "Read Only"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    crmContractAndFinancialDetailsAccess: role,
                  })),
              },
            ],
          },
          {
            header: "Field Sales",
            lines: [
              {
                type: "dropdown",
                label: "Access",
                options: ["CBS", "Power User", "FSM", "DOO"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    fieldSalesAccess: role,
                  })),
              },
            ],
          },
        ],
      },
      {
        jobTitle: "General Manager",
        header: licenseBundleName("Executive", "Executive"),
        systemsIncluded: "CRM, Email, Power BI, Solver",
        accessDetails: [
          {
            header: "CRM",
            lines: [{ type: "text", label: "Contract & Financial Details", value: "Full Edit Access" }],
          },
          {
            header: "Field Sales",
            lines: [{ type: "text", label: "Access", value: "Power User" }],
          },
        ],
      },
      {
        jobTitle: "Human Resources",
        header: licenseBundleName("Admin", "Administrative User"),
        systemsIncluded: "CRM, Email",
        accessDetails: [
          {
            header: "CRM",
            lines: [
              {
                type: "dropdown",
                label: "Contract & Financial Details",
                options: ["Full Edit Access", "Read Only", "None"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    crmContractAndFinancialDetailsAccess: role,
                  })),
              },
            ],
          },
        ],
      },
      {
        jobTitle: "Office Manager",
        header: licenseBundleName("Admin", "Administrative User"),
        systemsIncluded: "CRM, Email",
        accessDetails: [
          {
            header: "CRM",
            lines: [
              {
                type: "dropdown",
                label: "Contract & Financial Details",
                options: ["Full Edit Access", "Read Only", "None"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    crmContractAndFinancialDetailsAccess: role,
                  })),
              },
            ],
          },
          {
            header: "Field Sales",
            lines: [
              {
                type: "dropdown",
                label: "Access",
                options: ["N/A", "Power User"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    fieldSalesAccess: role,
                  })),
              },
            ],
          },
        ],
      },
      {
        jobTitle: "IC Recruiter",
        header: licenseBundleName("Admin", "Administrative User"),
        systemsIncluded: "CRM, Email",
        accessDetails: [
          {
            header: "CRM",
            lines: [
              {
                type: "dropdown",
                label: "Contract & Financial Details",
                options: ["Full Edit Access", "Read Only", "None"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    crmContractAndFinancialDetailsAccess: role,
                  })),
              },
            ],
          },
          {
            header: "Field Sales",
            lines: [
              {
                type: "dropdown",
                label: "Access",
                options: ["N/A", "Power User"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    fieldSalesAccess: role,
                  })),
              },
            ],
          },
        ],
      },
      {
        jobTitle: "TI Sales",
        header: <span>License Bundle dependent on user's job responsibilities and security access.</span>,
        accessDetails: [
          {
            header: "Business Central",
            lines: [
              {
                type: "dropdown",
                label: "Security Role",
                options: ["Accounting Clerk", "Collections", "Controller", "N/A"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    businessCentralSecurityRole: role,
                  })),
              },
            ],
          },
          {
            header: "CRM",
            lines: [
              {
                type: "dropdown",
                label: "Contract and Financial Details",
                options: ["Full Edit Access", "Read Only", "None"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    crmContractAndFinancialDetailsAccess: role,
                  })),
              },
              {
                type: "checkbox",
                label: "Ability to Approve Clients and Contracts",
                onClick: () =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    crmClientsAndContracts: !user.crmClientsAndContracts,
                  })),
                checked: user.crmClientsAndContracts,
              },
            ],
          },
          {
            header: "Field Sales",
            lines: [
              {
                type: "dropdown",
                label: "Access",
                options: ["FSM", "DOO", "Power User", "N/A"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    fieldSalesAccess: role,
                  })),
              },
            ],
          },
          {
            header: "Power BI",
            lines: [
              {
                type: "checkbox",
                label: "Should the user have access?",
                onClick: () =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    powerBi: !user.powerBi,
                  })),
                checked: user.powerBi,
              },
            ],
          },
          {
            header: "Jet Hub",
            lines: [
              {
                type: "checkbox",
                label: "Should the user have access?",
                onClick: () =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    jetHub: user.jetHub === "True" ? "False" : "True",
                  })),
                checked: user.jetHub === "True",
              },
            ],
          },
          {
            header: "Avalara",
            lines: [
              {
                type: "checkbox",
                label: "Should the user have access?",
                onClick: () =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    avalara: !user.avalara,
                  })),
                checked: user.avalara,
              },
            ],
          },
        ],
      },
      {
        jobTitle: "Other (Please Specify)",
        header: <span>License Bundle dependent on user's job responsibilities and security access.</span>,
        accessDetails: [
          {
            header: "Business Central",
            lines: [
              {
                type: "dropdown",
                label: "Security Role",
                options: ["Accounting Clerk", "Collections", "Controller", "N/A"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    businessCentralSecurityRole: role,
                  })),
              },
            ],
          },
          {
            header: "CRM",
            lines: [
              {
                type: "dropdown",
                label: "Contract and Financial Details",
                options: ["Full Edit Access", "Read Only", "None"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    crmContractAndFinancialDetailsAccess: role,
                  })),
              },
              {
                type: "checkbox",
                label: "Ability to Approve Clients and Contracts",
                onClick: () =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    crmClientsAndContracts: !user.crmClientsAndContracts,
                  })),
                checked: user.crmClientsAndContracts,
              },
            ],
          },
          {
            header: "Field Sales",
            lines: [
              {
                type: "dropdown",
                label: "Access",
                options: ["FSM", "DOO", "Power User", "N/A"],
                onChange: (role) =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    fieldSalesAccess: role,
                  })),
              },
            ],
          },
          {
            header: "Power BI",
            lines: [
              {
                type: "checkbox",
                label: "Should the user have access?",
                onClick: () =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    powerBi: !user.powerBi,
                  })),
                checked: user.powerBi,
              },
            ],
          },
          {
            header: "Jet Hub",
            lines: [
              {
                type: "checkbox",
                label: "Should the user have access?",
                onClick: () =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    jetHub: user.jetHub === "True" ? "False" : "True",
                  })),
                checked: user.jetHub === "True",
              },
            ],
          },
          {
            header: "Avalara",
            lines: [
              {
                type: "checkbox",
                label: "Should the user have access?",
                onClick: () =>
                  setUser((prevUser) => ({
                    ...prevUser,
                    avalara: !user.avalara,
                  })),
                checked: user.avalara,
              },
            ],
          },
        ],
      },
    ],
    [user, mode, setUser, typeOfModal, updateLocationsSupporting, licenseBundleName],
  );

  return (
    <div>
      {jobs
        .filter((job) => user && user.jobTitle === job.jobTitle)
        .map((job) => {
          return (
            <div
              style={{
                border: "1px solid #CED4DA",
                borderRadius: "5px",
                padding: "10px",
                minHeight: "323px",
                // 323 is the height of the search bar plus the height of the drop down
              }}
            >
              {job.header}
              <div style={{ padding: "10px 0px 0px 10px" }}>
                {job.systemsIncluded && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "5px",
                      borderBottom: job.accessDetails && "1px solid #CED4DA",
                      paddingBottom: "10px",
                    }}
                  >
                    <u>
                      <span>Systems Included:</span>
                    </u>
                    <span>- {job.systemsIncluded}</span>
                  </div>
                )}
                {job.accessDetails && renderAccessDetails(job.accessDetails)}
              </div>
            </div>
          );
        })}
      {user && !user.jobTitle && (
        <div
          style={{
            border: "1px solid #CED4DA",
            borderRadius: "5px",
            padding: "5px 10px 10px 10px",
            minHeight: "323px",
            textAlign: "center",
          }}
        >
          <span>Please Select a Job</span>
        </div>
      )}
      {user && user.jobTitle && jobs.filter((job) => user.jobTitle === job.jobTitle).length === 0 && (
        <div
          style={{
            border: "1px solid #CED4DA",
            borderRadius: "5px",
            padding: "5px 10px 10px 10px",
            minHeight: "323px",
            textAlign: "center",
          }}
        >
          <span>No Additional Information</span>
        </div>
      )}
    </div>
  );
};
export default JobInformationSection;
